import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  UploadOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons'
import { Button, Upload, Typography, Space, message, Spin } from 'antd'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  getTechnicalDrawing,
  useDeleteTechnicalDrawing,
  usePostTechnicalDrawing,
} from '$api/client'
import Tooltip from '$components/Tooltip'
import { checkFileSize } from '$utils'

const { Title } = Typography

const ALLOWED_FILE_TYPES = [
  'image/png',
  'image/jpeg',
  'image/tiff',
  'application/pdf',
]

const TechnicalDrawingUpload = ({ record }) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const queryKeyParts = 'parts'
  const queryKeyTechnicalDrawing = 'technicalDrawing'
  const [fileList, setFileList] = useState([])
  const [isUploading, setIsUploading] = useState(false)

  const beforeUpload = (file) => {
    if (!checkFileSize(file.size, '20 MB', t)) return Upload.LIST_IGNORE

    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      message.error(t('part_technical_drawing.file_type_not_allowed'))
      return Upload.LIST_IGNORE
    }
    return true
  }

  const { data: technicalDrawing, isFetching } = useQuery({
    queryKey: [queryKeyTechnicalDrawing, record.id],
    queryFn: ({ queryKey: [, id] }) => getTechnicalDrawing(id),
    enabled: !!record.technical_drawing,
  })

  const { mutate: deleteTechnicalDrawingMutation } = useDeleteTechnicalDrawing(
    queryKeyParts,
    queryKeyTechnicalDrawing,
  )

  const { mutate: postTechnicalDrawingMutation } = usePostTechnicalDrawing(
    queryKeyParts,
    queryKeyTechnicalDrawing,
  )

  const customUpload = ({ file, onProgress, onSuccess, onError }) => {
    setIsUploading(true)
    postTechnicalDrawingMutation(
      { partId: record.id, file, onProgress },
      {
        onSuccess: (data) => {
          setIsUploading(false)
          message.success(t('upload_success', 'File uploaded successfully'))
          if (onSuccess) {
            onSuccess(data)
          }
          setFileList([
            {
              uid: '1',
              name: file.name,
              status: 'done',
              url: URL.createObjectURL(file),
            },
          ])
          queryClient.invalidateQueries([queryKeyTechnicalDrawing])
        },
        onError: (err) => {
          setIsUploading(false)
          setFileList([])
          if (onError) {
            onError(err)
          }
        },
      },
    )
  }

  const onRemove = () => {
    deleteTechnicalDrawingMutation(record.id, {
      onSuccess: () => {
        setFileList([])
        message.success(t('delete_success', 'File deleted successfully'))
      },
    })
  }

  const handleChange = ({ fileList: newFileList }) => {
    setFileList(newFileList)
  }

  const handlePreview = (file) => {
    const url = file.url
    const a = document.createElement('a')
    a.href = url
    a.download = file.name
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }

  useEffect(() => {
    if (technicalDrawing) {
      setFileList((prevList) => [
        {
          uid: prevList.length > 0 ? prevList[0].uid : '1',
          name: technicalDrawing.filename,
          status: 'done',
          url: URL.createObjectURL(
            technicalDrawing.data,
            technicalDrawing.type,
          ),
        },
      ])
    }
  }, [technicalDrawing])

  const isLoading = isUploading || isFetching

  return (
    <>
      <Space size="small">
        <Title style={{ fontSize: '12px' }}>
          {t('part_technical_drawing.title')}
        </Title>
        <Tooltip title={t('part_technical_drawing.tooltip')}>
          <InfoCircleOutlined style={{ fontSize: '14px', marginTop: '5px' }} />
        </Tooltip>
        {isLoading && (
          <Spin indicator={<LoadingOutlined style={{ fontSize: 16 }} spin />} />
        )}
      </Space>
      <Upload
        accept=".png,.pdf,.jpeg,.jpg,.tiff,.tif"
        fileList={fileList}
        customRequest={customUpload}
        onChange={handleChange}
        beforeUpload={beforeUpload}
        onRemove={onRemove}
        showUploadList={{ showRemoveIcon: true }}
        onPreview={handlePreview}
        disabled={isLoading}
      >
        {fileList.length === 0 && (
          <Button icon={<UploadOutlined />} loading={isLoading}>
            {t('part_technical_drawing.upload_button')}
          </Button>
        )}
      </Upload>
    </>
  )
}
export default TechnicalDrawingUpload
